import { adminApiClient, type AdminApiClient } from './client';

export type CatalogueLocale = 'en';

export interface CatalogueFixture {
  externalId: string;
  name: string;
  localisedName: string | null;
  homeTeam: string;
  awayTeam: string;
  startTime: string;
  status: 'scheduled' | 'live' | 'finished' | 'settled';
  competitionId: string | null;
  competitionName: string | null;
  sportCode: string | null;
  /**
   * How many markets the feed holds for this fixture, and how many outcomes
   * those markets carry — counted server-side (CAE-1551).
   *
   * The browser used to total these itself: one `listMarketsForFixture` per
   * row, and nothing at all for the outcomes, which would have been one more
   * request per market. That is the 282-request fan-out CAE-1573 took off the
   * Contest Overview, and the Add Fixtures modal needs both figures for every
   * row on screen at once.
   */
  marketCount: number;
  outcomeCount: number;
}

export interface CatalogueMarket {
  externalId: string;
  fixtureExternalId: string;
  type: string;
  name: string;
  localisedName: string | null;
  /** `markets.display_name_override` — admin display label for this market in the feed */
  displayNameOverride: string | null;
}

export interface CatalogueOutcome {
  externalId: string;
  marketExternalId: string;
  fixtureExternalId: string | null;
  name: string;
  localisedName: string | null;
  decimalPrice: number | null;
  fractionalPrice: string | null;
  americanPrice: number | null;
  active: boolean | null;
}

/**
 * Whether the feed has delivered any markets for a fixture — the Catalogue
 * sidebar's Markets facet (CAE-1555).
 *
 * CAE-1551 left the Add Fixtures modal without this control because the
 * endpoint had no such filter and every row wears a `MarketBadge` saying which
 * it is. The Catalogue's own sidebar asks for it by name, so the endpoint grew
 * one.
 */
export type MarketsFilter = 'synced' | 'none';

export interface ListCatalogueFixturesFilters {
  name?: string;
  competition?: string;
  sport?: string;
  status?: string;
  markets?: MarketsFilter;
  from?: string;
  to?: string;
  locale?: CatalogueLocale;
  limit?: number;
  offset?: number;
}

export interface CatalogueFilterOptions {
  competitions: Array<{ id: string; name: string }>;
  sports: string[];
}

export interface CompetitionSyncResult {
  competitionId: string;
  competitionName: string;
  sport: string;
  fixtures: number;
  /** The competition's events that are not games (futures, specials, outrights), left out. */
  skipped: number;
  removed: number;
  status: 'ok' | 'error';
  error?: string;
}

export interface SportSyncError {
  sport: string;
  error: string;
}

export interface SyncFixturesResult {
  fixtures: number;
  skipped: number;
  removed: number;
  competitions: CompetitionSyncResult[];
  sportErrors: SportSyncError[];
}

export interface SyncMarketsResult {
  markets: number;
  outcomes: number;
  /** Stored markets of the synced fixtures the run no longer keeps, deleted with their outcomes. */
  removed: number;
  skipped: Array<{ fixtureExternalId: string; reason: string }>;
}

export type CatalogueSyncOutcome = 'succeeded' | 'partial' | 'failed';

/** A recorded catalogue sync (CAE-1597). */
export interface CatalogueSyncRun {
  outcome: CatalogueSyncOutcome;
  startedAt: string;
  finishedAt: string;
  counts: Record<string, number>;
  /** Fixture runs only: each Sport's part of the run. */
  sportResults: Array<{
    sportId: string;
    sport: string;
    outcome: CatalogueSyncOutcome;
    fixtures: number;
    removed: number;
    error?: string;
  }> | null;
  error: string | null;
}

export interface CatalogueSyncStatus {
  latest: CatalogueSyncRun | null;
  /** When the newest run the feed answered, in whole or in part, finished. */
  lastAnsweredAt: string | null;
}

export interface CatalogueSyncRuns {
  fixtures: CatalogueSyncStatus;
  markets: CatalogueSyncStatus;
}

export interface CatalogueApi {
  getFilterOptions: () => Promise<CatalogueFilterOptions>;
  listFixtures: (
    filters?: ListCatalogueFixturesFilters,
  ) => Promise<{ items: CatalogueFixture[]; total: number }>;
  listMarketsForFixture: (
    fixtureExternalId: string,
    locale?: CatalogueLocale,
  ) => Promise<CatalogueMarket[]>;
  listOutcomesForMarket: (
    marketExternalId: string,
    locale?: CatalogueLocale,
  ) => Promise<CatalogueOutcome[]>;
  updateMarketDisplayNameOverride: (
    marketExternalId: string,
    displayNameOverride: string | null,
    locale?: CatalogueLocale,
  ) => Promise<CatalogueMarket>;
  syncFixtures: () => Promise<SyncFixturesResult>;
  syncMarkets: (fixtureExternalIds: string[]) => Promise<SyncMarketsResult>;
  getSyncRuns: () => Promise<CatalogueSyncRuns>;
}

const buildQuery = (params: Record<string, string | undefined>): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      searchParams.set(key, value);
    }
  }
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const createCatalogueApi = (client: AdminApiClient = adminApiClient): CatalogueApi => ({
  getFilterOptions: async () => {
    return client.request<CatalogueFilterOptions>('/admin/catalogue/filter-options');
  },
  listFixtures: async (filters) => {
    const query = buildQuery({
      name: filters?.name,
      competition: filters?.competition,
      sport: filters?.sport,
      status: filters?.status,
      markets: filters?.markets,
      from: filters?.from,
      to: filters?.to,
      locale: filters?.locale,
      limit: filters?.limit != null ? String(filters.limit) : undefined,
      offset: filters?.offset != null ? String(filters.offset) : undefined,
    });
    return client.request<{ items: CatalogueFixture[]; total: number }>(
      `/admin/catalogue/fixtures${query}`,
    );
  },
  listMarketsForFixture: async (fixtureExternalId, locale) => {
    const query = buildQuery({ locale });
    const response = await client.request<{ items: CatalogueMarket[] }>(
      `/admin/catalogue/fixtures/${encodeURIComponent(fixtureExternalId)}/markets${query}`,
    );
    return response.items;
  },
  listOutcomesForMarket: async (marketExternalId, locale) => {
    const query = buildQuery({ locale });
    const response = await client.request<{ items: CatalogueOutcome[] }>(
      `/admin/catalogue/markets/${encodeURIComponent(marketExternalId)}/outcomes${query}`,
    );
    return response.items;
  },
  updateMarketDisplayNameOverride: async (marketExternalId, displayNameOverride, locale) => {
    const query = buildQuery({ locale: locale ?? 'en' });
    return client.request<CatalogueMarket>(
      `/admin/catalogue/markets/${encodeURIComponent(marketExternalId)}/display-name-override${query}`,
      { method: 'PATCH', body: { displayNameOverride } },
    );
  },
  syncFixtures: async () => {
    return client.request<SyncFixturesResult>('/admin/catalogue/sync-fixtures', {
      method: 'POST',
    });
  },
  syncMarkets: async (fixtureExternalIds) => {
    return client.request<SyncMarketsResult>('/admin/catalogue/sync-markets', {
      method: 'POST',
      body: { fixtureExternalIds },
    });
  },
  getSyncRuns: async () => {
    return client.request<CatalogueSyncRuns>('/admin/catalogue/sync-runs');
  },
});

export const catalogueApi = createCatalogueApi();
